import { useId, useRef, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { formatTimestamp } from "@/lib/routineTypes";

export function RoutineTrimTimeline({
  name, duration, start, end, playhead, disabled, onChange, onSeek, onInteractionStart,
}: {
  name: string;
  duration: number;
  start: number | null;
  end: number | null;
  playhead: number;
  disabled: boolean;
  onChange: (start: number, end: number) => void;
  onSeek: (seconds: number, play?: boolean) => void;
  onInteractionStart: () => void;
}) {
  const track = useRef<HTMLDivElement>(null);
  const drag = useRef<{ field: "start" | "end"; value: number; offset: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const hint = useId();
  const selected = start !== null && end !== null;
  const left = start ?? 0;
  const right = end ?? duration;
  const gap = Math.min(0.1, duration);
  const percent = (time: number) => `${Math.max(0, Math.min(100, time / duration * 100))}%`;
  const update = (field: "start" | "end", value: number) => {
    const next = Math.round(Math.max(field === "start" ? 0 : left + gap,
      Math.min(field === "start" ? right - gap : duration, value)) * 1000) / 1000;
    onChange(field === "start" ? next : left, field === "end" ? next : right);
    return next;
  };
  const move = (clientX: number) => {
    if (!drag.current || !track.current || disabled) return;
    const bounds = track.current.getBoundingClientRect();
    const value = update(drag.current.field, (clientX - drag.current.offset - bounds.left) / bounds.width * duration);
    if (value !== drag.current.value) onSeek(value);
    drag.current.value = value;
  };

  return (
    <div className="rounded-xl border border-[#e3dce1] bg-[#faf7f9] p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-[#685461]">
          <span className={`h-2 w-2 rounded-full ${selected ? "bg-[#762a5c]" : "bg-amber-500"}`} />
          {selected ? "Selected portion" : "Choose a portion to include"}
        </div>
        <div className="flex items-center gap-1">
          <span className="mr-1 font-mono text-[11px] tabular-nums text-[#685461]">{formatTimestamp(playhead)}</span>
          <button type="button" aria-label={`Zoom out timeline for ${name}`} disabled={zoom === 1 || disabled}
            className="rounded-md p-2 hover:bg-white disabled:opacity-30" onClick={() => setZoom(zoom / 2)}><Minus size={14} /></button>
          <span className="w-5 text-center text-[11px]">{zoom}×</span>
          <button type="button" aria-label={`Zoom in timeline for ${name}`} disabled={zoom === 4 || disabled}
            className="rounded-md p-2 hover:bg-white disabled:opacity-30" onClick={() => setZoom(zoom * 2)}><Plus size={14} /></button>
        </div>
      </div>
      <div className="overflow-x-auto pb-2">
        <div className="px-6" style={{ width: `${zoom * 100}%` }}>
          <div className="relative mb-2 h-4 text-[10px] tabular-nums text-[#85727f]" aria-hidden="true">
            {Array.from({ length: 4 * zoom + 1 }, (_, i) => (
              <span key={i} className="absolute bottom-0 -translate-x-1/2 border-b border-[#d7cbd2] pb-1"
                style={{ left: `${i / (4 * zoom) * 100}%` }}>{formatTimestamp(duration * i / (4 * zoom)).slice(0, -4)}</span>
            ))}
          </div>
          <div ref={track} className="relative h-8 rounded bg-[#e6dfe4] focus-within:ring-2 focus-within:ring-[#b68aa7] focus-within:ring-offset-2">
            <div className={`pointer-events-none absolute inset-y-0 border-y ${selected ? "border-[#762a5c] bg-[#b785a5]/40" : "border-[#bda5b4] bg-[#d4c3ce]/40"}`}
              style={{ left: percent(left), width: percent(right - left) }} />
            <input type="range" min={0} max={duration} step={0.01} value={Math.max(0, Math.min(duration, playhead))}
              aria-label={`Playhead for ${name}`} aria-valuetext={formatTimestamp(playhead)} disabled={disabled}
              className="absolute inset-0 z-10 h-full w-full cursor-crosshair opacity-0"
              onKeyDown={(e) => {
                const step = e.shiftKey ? 1 : 0.1;
                const next = { Home: 0, End: duration, ArrowLeft: playhead - step,
                  ArrowDown: playhead - step, ArrowRight: playhead + step, ArrowUp: playhead + step }[e.key];
                if (next === undefined) return;
                e.preventDefault();
                onSeek(Math.max(0, Math.min(duration, next)));
              }}
              onChange={(e) => onSeek(Number(e.target.value))} />
            <div className="pointer-events-none absolute inset-y-[-5px] z-10 w-0.5 bg-[#321b29]"
              style={{ left: percent(playhead) }}><span className="absolute -left-1 -top-1 h-2.5 w-2.5 rotate-45 rounded-sm bg-[#321b29]" /></div>
            {(["start", "end"] as const).map((field) => (
              <button key={field} type="button" role="slider" aria-label={`${field === "start" ? "Start" : "End"} marker for ${name}`}
                aria-orientation="horizontal" aria-valuemin={field === "start" ? 0 : left + gap}
                aria-valuemax={field === "start" ? right - gap : duration} aria-valuenow={field === "start" ? left : right}
                aria-valuetext={formatTimestamp(field === "start" ? left : right)} aria-describedby={hint}
                disabled={disabled} style={{ left: percent(field === "start" ? left : right), touchAction: "none" }}
                className={`absolute -inset-y-1 z-20 flex w-4 ${field === "start" ? "-translate-x-full" : ""} cursor-ew-resize items-center justify-center rounded border border-white bg-[#762a5c] text-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#762a5c] disabled:cursor-default disabled:opacity-40`}
                onPointerDown={(e) => {
                  if (e.button !== 0 || disabled) return;
                  e.preventDefault();
                  e.currentTarget.focus();
                  e.currentTarget.setPointerCapture(e.pointerId);
                  onInteractionStart();
                  const bounds = track.current!.getBoundingClientRect();
                  const value = field === "start" ? left : right;
                  drag.current = { field, value, offset: e.clientX - bounds.left - value / duration * bounds.width };
                }}
                onPointerMove={(e) => move(e.clientX)}
                onPointerUp={(e) => {
                  if (!drag.current) return;
                  move(e.clientX);
                  onSeek(drag.current.value, true);
                  drag.current = null;
                  e.currentTarget.releasePointerCapture(e.pointerId);
                }}
                onPointerCancel={() => { drag.current = null; }}
                onLostPointerCapture={() => { drag.current = null; }}
                onKeyDown={(e) => {
                  const value = field === "start" ? left : right;
                  const step = e.shiftKey ? 1 : 0.1;
                  const next = { ArrowLeft: value - step, ArrowDown: value - step, ArrowRight: value + step,
                    ArrowUp: value + step, Home: 0, End: duration }[e.key];
                  if (next === undefined) return;
                  e.preventDefault();
                  onInteractionStart();
                  onSeek(update(field, next), true);
                }}><span aria-hidden="true" className="h-3 w-0.5 rounded-full bg-white/80" /></button>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-1 flex flex-wrap justify-between gap-1 text-[11px] text-[#685461]">
        <span className="font-mono tabular-nums">{selected ? `${formatTimestamp(left)} → ${formatTimestamp(right)}` : "No range selected"}</span>
        <span>{selected ? `${formatTimestamp(right - left)} selected` : `${formatTimestamp(duration)} total`}</span>
      </div>
      <p id={hint} className="mt-2 text-[11px] text-[#85727f]">
        Drag to trim, release to play · Click the track to seek
        <span className="sr-only">. Use arrow keys for precise adjustments; Shift moves by one second.</span>
      </p>
    </div>
  );
}
